import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xScreen } from "@stoneage/XDL/Utilities";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductDetailsViewStyles {
    container?: InjectableCss;
    firstTwoThirdsColumn?: InjectableCss;
    leftColumnGridItem?: InjectableCss;
    leftColumnInnerContainer?: InjectableCss;
    topGridItem?: InjectableCss;
    middleGridItem?: InjectableCss;
    bottomGridItem?: InjectableCss;
    rightColumnGridItem?: InjectableCss;
    productDetailsImagesContainer?: InjectableCss;
    productDetailsTitleDesktop?: InjectableCss;
    productDetailsTitleMobile?: InjectableCss;
    productDetailsContent?: InjectableCss;
    productDetailsAttributes?: InjectableCss;
    productDetailsConfigurations?: InjectableCss;
    productActionsMobile?: InjectableCss;
    productDetailsResources?: InjectableCss;
    productDetailsCarousels?: InjectableCss;
    productDetailsStickyActions?: InjectableCss;
    productDetailsActions?: InjectableCss;
}

export const viewStyles: ProductDetailsViewStyles = {
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
        `,
    },
    firstTwoThirdsColumn: {
        css: css`
            flex-grow: 1;
            display: flex;
            flex-wrap: wrap;
            margin-right: 22px;
            max-width: calc(100% - 330px);
            ${xScreen('lte', 'md')} {
                max-width: 100%;
                margin: 0;
            }
            @media print {
                max-width: 100%;
                margin: 0;
            }
        `,
    },
    leftColumnGridItem: {
        css: css`
            width: 288px;
            margin-right: 42px;
            ${xScreen('lte', 'md')} {
                display: flex;
                width: 100%;
                margin: 0;
                justify-content: space-between;
                margin-bottom: 32px;
            }
            @media all and (max-width: 700px) {
                flex-wrap: wrap;
                justify-content: center;
            }
            @media print {
                flex-wrap: wrap;
                justify-content: center;
            }
        `,
    },
    middleGridItem: {
        css: css`
            flex-grow: 1;
            max-width: calc(100% - 330px);
            ${xScreen('lte', 'md')} {
                max-width: 100%;
            }
        `,
    },
    rightColumnGridItem: {
        css: css`
            width: 308px;
            display: block;
            min-height: 100%;
            ${xScreen('lte', 'md')} {
                display: none;
            }
        `,
    },
    productDetailsResources: {
        css: css`
            width: 100%;
            margin: 64px 0 0;
            @media print {
                && div[class*='GridItemStyle-sc'] {
                    display: block;
                }
            }
        `,
    },
    productDetailsCarousels: {
        css: css`
            width: 100%;
        `,
    },
    productDetailsConfigurations: {
        css: css`
            width: 100%;
        `,
    },
    productDetailsTitleDesktop: {
        css: css`
            display: block;
            ${xScreen('lte', 'md')} {
                display: none;
            }
            @media print {
                display: none;
            }
        `,
    },
    productDetailsTitleMobile: {
        css: css`
            display: none;
            ${xScreen('lte', 'md')} {
                display: block;
                width: 100%;
            }
            @media print {
                display: block;
            }
        `,
    },
    productActionsMobile: {
        css: css`
            display: none;
            ${xScreen('lte', 'md')} {
                display: block;
                max-width: 352px;
                width: 100%;
            }
            @media all and (max-width: 700px) {
                max-width: 100%;
                width: 100%;
            }
            @media print {
                display: block;
                max-width: 100%;
                width: 100%;
            }
        `,
    },
    productDetailsStickyActions: {
        css: css`
            position: sticky;
            top: 105px;
        `,
    },
    productDetailsActions: {
        css: css`
            background: ${getColor('common.ui4')};
            padding: 16px;
            margin-bottom: 12px;
        `,
    },
    productDetailsImagesContainer: {
        css: css`
            min-height: 100%;
            ${xScreen('lte', 'md')} {
                max-width: 312px;
                margin-right: 56px;
            }
            @media all and (max-width: 700px) {
                min-height: auto;
                max-width: 100%;
                margin-right: 0;
                margin-bottom: 24px;
            }
            @media print {
                display: block;
                min-height: auto;
                max-width: 100%;
                margin-right: 0;
                margin-bottom: 24px;
            }
        `,
    },
};

const stickyStyles = css`
    position: sticky;
    top: 105px;
    ${xScreen('lte', 'md')} {
        position: static;
    }
`;

const styles = viewStyles;

const ProductDetailsView: React.FC<WidgetProps> = ({ id }) => {
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.productDetailsTitleMobile}>
                <Zone contentId={id} zoneName="ProductDetailsTitle" />
            </StyledWrapper>
            <StyledWrapper {...styles.firstTwoThirdsColumn}>
                <StyledWrapper {...styles.leftColumnGridItem}>
                    <StyledWrapper {...styles.productDetailsImagesContainer}>
                        <StyledWrapper css={stickyStyles}>
                            <Zone contentId={id} zoneName="ProductDetailsPrimaryImageAndImageCarousel" />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.productActionsMobile}>
                        <StyledWrapper {...styles.productDetailsActions}>
                            <Zone contentId={id} zoneName="ProductDetailsActions" />
                        </StyledWrapper>
                        <Zone contentId={id} zoneName="productDetailsShareEmailAndCopy" />
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...styles.middleGridItem}>
                    <StyledWrapper {...styles.productDetailsTitleDesktop}>
                        <Zone contentId={id} zoneName="ProductDetailsTitle" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.productDetailsContent}>
                        <Zone contentId={id} zoneName="ProductDetailsMetaContent" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.productDetailsAttributes}>
                        <Zone contentId={id} zoneName="ProductDetailsAttributes" />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.productDetailsConfigurations}>
                    <Zone contentId={id} zoneName="ProductDetailsConfigurations" />
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.rightColumnGridItem}>
                <StyledWrapper {...styles.productDetailsStickyActions}>
                    <StyledWrapper {...styles.productDetailsActions}>
                        <Zone contentId={id} zoneName="ProductDetailsActions" />
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="productDetailsShareEmailAndCopy" />
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.productDetailsResources}>
                <Zone contentId={id} zoneName="ProductDetailsDocuments" />
            </StyledWrapper>
            <StyledWrapper {...styles.productDetailsCarousels}>
                <Zone contentId={id} zoneName="productDetailsCarousels" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect()(ProductDetailsView),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
